<template>
<div class="clone-metric growth-card" :class="{ 'growth-card--disabled': disabled }" :title="title">
  <div class="clone-metric__label">
    <span class="clone-metric__dot" :style="{ backgroundColor: color }"></span>
    <span>{{ label }}</span>
    <span v-if="unit" class="clone-metric__unit">{{ unit }}</span>
  </div>

  <!-- THE BAND, TYPED OR DRAGGED: the lowest and the highest growth expected over the next twelve
       months, as two fields and one slider over the same pair. The readings beside them are the band
       said in money, against the year the steps before this one stated. -->
  <div class="clone-metric__headline growth-card__headline">
    <div class="growth-card__field">
      <label class="clone-metric__ask" :for="`${fieldId}_min`">{{ texts.min }}</label>
      <v-text-field
        :id="`${fieldId}_min`"
        ref="minField"
        :key="`${inputKey}_min`"
        :value="fieldValue(shown.min)"
        class="clone-metric__input growth-card__input"
        suffix="%"
        maxlength="6"
        hide-details
        single-line
        :disabled="disabled"
        @change="onField('min', $event)"
      ></v-text-field>
    </div>
    <div class="growth-card__field">
      <label class="clone-metric__ask" :for="`${fieldId}_max`">{{ texts.max }}</label>
      <v-text-field
        :id="`${fieldId}_max`"
        :key="`${inputKey}_max`"
        :value="fieldValue(shown.max)"
        class="clone-metric__input growth-card__input"
        suffix="%"
        maxlength="6"
        hide-details
        single-line
        :disabled="disabled"
        @change="onField('max', $event)"
      ></v-text-field>
    </div>
    <div class="clone-metric__aside">
      <span class="clone-metric__ask clone-metric__ask--static">{{ texts.pastYear }}</span>
      <span class="clone-metric__aside-value growth-card__reading">{{ format(pastTotal) }}</span>
    </div>
    <div class="clone-metric__aside">
      <span class="clone-metric__ask clone-metric__ask--static">{{ texts.nextYear }}</span>
      <span class="clone-metric__aside-value growth-card__reading" :style="{ color: color }">{{ nextRange }}</span>
      <p class="clone-metric__hint">{{ changeText }}</p>
    </div>
  </div>

  <v-range-slider
    :value="[shown.min, shown.max]"
    :min="sliderMin"
    :max="sliderMax"
    :step="1"
    :color="color"
    :track-color="trackColor"
    :track-fill-color="color"
    class="growth-card__slider"
    hide-details
    :thumb-label="!disabled"
    :disabled="disabled"
    @input="onSlide"
    @change="onSlideEnd">
    <template v-slot:thumb-label="{ value }">{{ signed(value) }}</template>
  </v-range-slider>

  <!-- LAST YEAR AGAINST THE NEXT ONE, month by month and in total. The months already stated stand
       solid on the left; the twelve to come stand on the right as the lowest growth, solid and paler,
       with the band up to the highest hatched over it, and a line across each at the same month last
       year so the difference is read without counting. The year's two totals are set apart at their
       own scale, the way the other cards set the year beside December. -->
  <div class="growth-card__groups">
    <span class="growth-card__group">{{ texts.pastGroup }}</span>
    <span class="growth-card__group growth-card__group--future">{{ texts.futureGroup }}</span>
    <span class="growth-card__group growth-card__group--apart"></span>
  </div>
  <div class="growth-card__plot">
    <div class="growth-card__months">
      <span
        v-for="bar in pastBars"
        :key="`past_${bar.index}`"
        class="growth-card__bar"
        :title="bar.title">
        <span class="growth-card__fill" :style="{ height: bar.height + '%', backgroundColor: color }"></span>
      </span>
    </div>
    <div class="growth-card__divider"></div>
    <div class="growth-card__months">
      <span
        v-for="bar in futureBars"
        :key="`future_${bar.index}`"
        class="growth-card__bar"
        :title="bar.title">
        <span class="growth-card__stack">
          <span class="growth-card__band" :style="{ height: bar.band + '%', backgroundImage: hatch }"></span>
          <span class="growth-card__fill growth-card__fill--future" :style="{ height: bar.low + '%', backgroundColor: color }"></span>
        </span>
        <span class="growth-card__mark" :style="{ bottom: bar.past + '%' }"></span>
      </span>
    </div>
    <div class="growth-card__apart">
      <span class="growth-card__bar" :title="yearBars.pastTitle">
        <span class="growth-card__fill" :style="{ height: yearBars.past + '%', backgroundColor: color }"></span>
      </span>
      <span class="growth-card__bar" :title="yearBars.nextTitle">
        <span class="growth-card__stack">
          <span class="growth-card__band" :style="{ height: yearBars.band + '%', backgroundImage: hatch }"></span>
          <span class="growth-card__fill growth-card__fill--future" :style="{ height: yearBars.low + '%', backgroundColor: color }"></span>
        </span>
        <span class="growth-card__mark" :style="{ bottom: yearBars.past + '%' }"></span>
      </span>
    </div>
  </div>
  <div class="growth-card__axis">
    <div class="growth-card__months">
      <span v-for="(name, index) in initials" :key="`past_tick_${index}`" class="growth-card__tick">{{ name }}</span>
    </div>
    <div class="growth-card__divider growth-card__divider--axis"></div>
    <div class="growth-card__months">
      <span v-for="(name, index) in initials" :key="`future_tick_${index}`" class="growth-card__tick">{{ name }}</span>
    </div>
    <div class="growth-card__apart">
      <span class="growth-card__tick growth-card__tick--word">{{ texts.pastColumn }}</span>
      <span class="growth-card__tick growth-card__tick--word">{{ texts.nextColumn }}</span>
    </div>
  </div>

  <div class="growth-card__legend">
    <span class="growth-card__key">
      <span class="growth-card__swatch" :style="{ backgroundColor: color }"></span>{{ texts.legendPast }}
    </span>
    <span class="growth-card__key">
      <span class="growth-card__swatch growth-card__swatch--future" :style="{ backgroundColor: color }"></span>
      <span class="growth-card__swatch" :style="{ backgroundImage: hatch }"></span>{{ texts.legendFuture }}
    </span>
    <span class="growth-card__key">
      <span class="growth-card__swatch growth-card__swatch--line"></span>{{ texts.legendMark }}
    </span>
  </div>
</div>
</template>

<script>
// ONE SERIES' GROWTH OVER THE NEXT TWELVE MONTHS, for the composer's Future Trends step: the band
// typed or dragged, what it comes to in money, and last year against the next one drawn side by side.
//
// Like the other cards it owns no rule about what may be stated: a typed field and a released slider
// go up as they are, and the parent parses, clamps and orders them. The one thing held here is the
// band while a thumb is being dragged, so the columns follow the pointer without a rebuild per pixel.

// The slider's own reach. A field can state more, up to what the payload accepts, and the slider then
// widens to hold it.
const SLIDER_REACH = 50

export default {
  name: 'EkrmGrowthCard',
  props: {
    label: { type: String, required: true },
    title: { type: String, default: '' },
    unit: { type: String, default: '' },
    color: { type: String, default: '#43A047' },
    // The twelve months already stated, in money.
    past: { type: Array, required: true },
    // The band, in signed percentages.
    min: { type: Number, default: 0 },
    max: { type: Number, default: 0 },
    // The month names, for the axis and the hover titles.
    months: { type: Array, required: true },
    // How a sum of money is written, the parent's own formatter.
    format: { type: Function, required: true },
    inputKey: { type: String, default: 'growth' },
    // Every word the card shows, already translated.
    texts: { type: Object, required: true },
    // Drawn but not answerable: the fields and the slider are off and the card is greyed, for a
    // question the wizard shows ahead of offering it.
    disabled: { type: Boolean, default: false }
  },
  data () {
    return {
      // The band while a thumb is held, or null.
      dragging: null
    }
  },
  computed: {
    fieldId () {
      return `ekrm_growth_${this.inputKey}`
    },
    shown () {
      return this.dragging || { min: this.min, max: this.max }
    },
    sliderMin () {
      return Math.min(-SLIDER_REACH, Math.floor(this.min))
    },
    sliderMax () {
      return Math.max(SLIDER_REACH, Math.ceil(this.max))
    },
    trackColor () {
      return this.color + '33'
    },
    hatch () {
      return `repeating-linear-gradient(135deg, ${this.color}99 0, ${this.color}99 2px, ${this.color}22 2px, ${this.color}22 5px)`
    },
    values () {
      return this.past.map(value => Math.max(0, Number(value) || 0))
    },
    initials () {
      return this.months.map(name => String(name || '').slice(0, 1))
    },
    lowFactor () {
      return 1 + this.shown.min / 100
    },
    highFactor () {
      return 1 + this.shown.max / 100
    },
    // One scale for both years' months, so a month that grows is visibly taller than the one it
    // grows from.
    monthScale () {
      const top = Math.max.apply(null, this.values.concat([0]))
      return top * Math.max(1, this.highFactor)
    },
    pastTotal () {
      return this.values.reduce((sum, value) => sum + value, 0)
    },
    pastBars () {
      const scale = this.monthScale
      return this.values.map((value, index) => ({
        index: index,
        height: scale > 0 ? value / scale * 100 : 0,
        title: `${this.months[index]} · ${this.texts.pastColumn}: ${this.format(value)}`
      }))
    },
    futureBars () {
      const scale = this.monthScale
      return this.values.map((value, index) => {
        const low = value * this.lowFactor
        const high = value * this.highFactor
        return {
          index: index,
          low: scale > 0 ? low / scale * 100 : 0,
          band: scale > 0 ? (high - low) / scale * 100 : 0,
          past: scale > 0 ? value / scale * 100 : 0,
          title: `${this.months[index]} · ${this.texts.nextColumn}: ${this.range(low, high)}`
        }
      })
    },
    // The two totals, at their own scale: a year is twelve months tall.
    yearBars () {
      const total = this.pastTotal
      const low = total * this.lowFactor
      const high = total * this.highFactor
      const scale = total * Math.max(1, this.highFactor)
      return {
        past: scale > 0 ? total / scale * 100 : 0,
        low: scale > 0 ? low / scale * 100 : 0,
        band: scale > 0 ? (high - low) / scale * 100 : 0,
        pastTitle: `${this.texts.pastColumn}: ${this.format(total)}`,
        nextTitle: `${this.texts.nextColumn}: ${this.range(low, high)}`
      }
    },
    nextRange () {
      return this.range(this.pastTotal * this.lowFactor, this.pastTotal * this.highFactor)
    },
    changeText () {
      const low = this.pastTotal * (this.lowFactor - 1)
      const high = this.pastTotal * (this.highFactor - 1)
      if (this.shown.min === 0 && this.shown.max === 0) return this.texts.noChange
      const said = Math.abs(high - low) < 0.5
        ? this.signedMoney(low)
        : `${this.signedMoney(low)} – ${this.signedMoney(high)}`
      return `${this.texts.change}: ${said}`
    }
  },
  methods: {
    focusValue () {
      const field = this.$refs.minField
      if (!field) return
      field.focus()
      const el = field.$refs.input
      if (el) el.select()
    },
    range (low, high) {
      if (Math.abs(high - low) < 0.5) return this.format(low)
      return `${this.format(low)} – ${this.format(high)}`
    },
    signed (value) {
      const number = Number(value) || 0
      if (number > 0) return `+${number}%`
      if (number < 0) return `−${Math.abs(number)}%`
      return '0%'
    },
    signedMoney (value) {
      if (value > 0) return `+${this.format(value)}`
      if (value < 0) return `−${this.format(Math.abs(value))}`
      return this.format(0)
    },
    fieldValue (value) {
      return String(Number(value) || 0)
    },
    onField (edge, value) {
      if (this.disabled) return
      this.$emit('input', {
        min: edge === 'min' ? value : this.min,
        max: edge === 'max' ? value : this.max
      })
    },
    onSlide (value) {
      if (this.disabled) return
      this.dragging = { min: value[0], max: value[1] }
    },
    onSlideEnd (value) {
      this.dragging = null
      if (this.disabled) return
      this.$emit('input', { min: value[0], max: value[1] })
    }
  }
}
</script>

<style scoped>
  .clone-metric {
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    padding: 12px 16px 10px;
  }
  .clone-metric__label {
    display: flex;
    align-items: center;
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-metric__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 7px;
    flex: none;
  }
  .clone-metric__unit {
    margin-left: 6px;
    text-transform: none;
    letter-spacing: normal;
    color: rgba(0, 0, 0, 0.38);
  }
  .clone-metric__headline {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 28px;
  }
  .clone-metric__ask {
    display: block;
    margin-top: 2px;
    font-size: 0.7rem;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;
  }
  .clone-metric__ask--static {
    cursor: default;
  }
  .clone-metric__hint {
    margin: 4px 0 0;
    font-size: 0.7rem;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.5);
  }
  .clone-metric__aside {
    flex: 0 0 auto;
    min-width: 0;
  }
  .clone-metric__aside-value {
    display: block;
    font-size: 1.6rem;
    font-weight: 500;
    line-height: 1.45;
    color: rgba(0, 0, 0, 0.55);
    white-space: nowrap;
  }
  .clone-metric__input {
    margin: 0;
    padding: 0;
  }
  .clone-metric__input >>> input {
    font-size: 1.6rem;
    font-weight: 500;
    line-height: 1.45;
    padding: 0;
    max-height: none;
  }
  .clone-metric__input >>> .v-text-field__suffix {
    font-size: 1.6rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-metric__input >>> .v-input__slot::before {
    border-color: rgba(0, 0, 0, 0.12);
  }

  /* Shown ahead of being offered: the whole card greyed, colours included, and nothing in it reacts. */
  .growth-card--disabled {
    filter: grayscale(1);
    opacity: 0.55;
    pointer-events: none;
    user-select: none;
  }
  .growth-card__field {
    flex: 0 0 auto;
  }
  /* Room for a signed three-digit percentage and its sign. */
  .growth-card__input {
    width: 7ch;
    font-size: 1.6rem;
  }
  /* A range is two figures and a dash, so it is set a size down from the headline. */
  .growth-card__reading {
    font-size: 1.3rem;
  }
  .growth-card__slider {
    margin-top: 10px;
  }

  /* The plot: twelve months, the rule, twelve months, and the two years set apart. */
  .growth-card__groups,
  .growth-card__plot,
  .growth-card__axis {
    display: flex;
    gap: 0;
  }
  .growth-card__groups {
    margin-top: 12px;
    font-size: 0.65rem;
    color: rgba(0, 0, 0, 0.5);
  }
  .growth-card__group {
    flex: 1 1 0;
    min-width: 0;
  }
  .growth-card__group--future {
    padding-left: 13px;
  }
  .growth-card__group--apart {
    flex: 0 0 132px;
  }
  .growth-card__plot {
    height: 140px;
    margin-top: 4px;
    align-items: stretch;
  }
  .growth-card__months {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    gap: 2px;
    align-items: stretch;
  }
  .growth-card__divider {
    flex: 0 0 1px;
    margin: 0 6px;
    border-left: 1px dashed rgba(0, 0, 0, 0.3);
  }
  .growth-card__divider--axis {
    border-left-color: transparent;
  }
  .growth-card__apart {
    flex: 0 0 120px;
    margin-left: 12px;
    padding-left: 12px;
    border-left: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    gap: 8px;
    align-items: stretch;
  }
  .growth-card__bar {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
  .growth-card__stack {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 100%;
  }
  .growth-card__fill,
  .growth-card__band {
    display: block;
    width: 100%;
    flex: none;
  }
  /* The lowest growth, paler than the months already stated: it is expected rather than said. */
  .growth-card__fill--future {
    opacity: 0.45;
  }
  /* The same month last year, across the column that grows from it. */
  .growth-card__mark {
    position: absolute;
    left: -1px;
    right: -1px;
    height: 0;
    border-top: 2px solid rgba(0, 0, 0, 0.6);
    pointer-events: none;
  }
  .growth-card__axis {
    margin-top: 3px;
  }
  .growth-card__tick {
    flex: 1 1 0;
    min-width: 0;
    text-align: center;
    font-size: 9px;
    line-height: 1.1;
    color: rgba(0, 0, 0, 0.45);
    overflow: hidden;
  }
  .growth-card__tick--word {
    font-size: 0.6rem;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .growth-card__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 18px;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.55);
  }
  .growth-card__key {
    display: inline-flex;
    align-items: center;
  }
  .growth-card__swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 5px;
    border-radius: 2px;
  }
  .growth-card__swatch--future {
    opacity: 0.45;
    margin-right: 2px;
  }
  .growth-card__swatch--line {
    height: 0;
    border-top: 2px solid rgba(0, 0, 0, 0.6);
    border-radius: 0;
  }
</style>
